import { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Input,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import AdminOnlyAccess from "../components/common/base/AdminOnlyAccess";
import Pagination from "../components/common/pagination";
import formatDate from "../components/common/utils";
import useFetch from "../components/shared/hooks/api/fetch";
import useDebouncedSearch from "../components/shared/hooks/useDebouncedSearch";
import { getInstallationsPaginatedEndpoint } from "../services/installationService";
import INSTALLATION_STEPS from "../components/installations/installationConstants";
import StepBadge from "../components/installations/StepBadge";
import { CopyButton } from "../components/common/buttons";

const PAGE_SIZE = 20;
const APP_URL = "https://veesion-carglass.vercel.app/install";

function SortableHeader({ label, field, ordering, onSort }) {
  const isAsc = ordering === field;
  const isDesc = ordering === `-${field}`;
  const iconClass = isAsc ? "fas fa-sort-up" : isDesc ? "fas fa-sort-down" : "fas fa-sort text-muted";
  return (
    <th role="button" onClick={() => onSort(field)} style={{ cursor: "pointer", userSelect: "none" }}>
      {label} <i className={iconClass} />
    </th>
  );
}

export default function InstallationListView() {
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const {
    inputValue: searchInput,
    debouncedValue: search,
    handleChange: handleSearchChange,
    reset: resetSearch,
  } = useDebouncedSearch();
  const [step, setStep] = useState("");
  const [idDisplay, setIdDisplay] = useState("deal_id");
  const [ordering, setOrdering] = useState("-updated_at");
  const [data] = useFetch(getInstallationsPaginatedEndpoint({ page: currentPage, search, step, ordering }));

  const handleSort = (field) => {
    setOrdering((prev) => (prev === field ? `-${field}` : field));
    setCurrentPage(1);
  };

  const handleStepChange = (value) => {
    setStep(value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    resetSearch();
    setStep("");
    setCurrentPage(1);
  };

  const hasActiveFilters = !!(search || step);
  const installations = data?.results ?? [];

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <CardTitle tag="h4" className="mb-0">
                    Installations
                  </CardTitle>
                  <small className="text-muted ml-3 mr-1">{APP_URL}</small>
                  <CopyButton value={APP_URL} />
                </div>
                <div>
                  <AdminOnlyAccess>
                    <Button
                      color="secondary"
                      size="sm"
                      className="mr-2"
                      onClick={() => history.push("/installation-settings")}
                    >
                      <i className="fas fa-cog mr-1" />
                      Settings
                    </Button>
                  </AdminOnlyAccess>
                  <Button color="primary" size="sm" onClick={() => history.push("/installations/new")}>
                    <i className="fas fa-plus mr-1" />
                    New
                  </Button>
                </div>
              </div>
              <Row className="align-items-center mb-2">
                <Col md="4">
                  <Input
                    placeholder="Search by deal ID, location, email…"
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.currentTarget.value)}
                  />
                </Col>
                <Col md="4">
                  <Pagination
                    itemsCount={data?.count ?? 0}
                    pageSize={PAGE_SIZE}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    label="installations"
                  />
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col md="2">
                  <Input type="select" value={step} onChange={(e) => handleStepChange(e.target.value)}>
                    <option value="">— All steps —</option>
                    {Object.entries(INSTALLATION_STEPS).map(([value, { label }]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="auto">
                  <ButtonGroup size="sm">
                    <Button
                      id="btn-deal-id"
                      color={idDisplay === "deal_id" ? "primary" : "secondary"}
                      outline={idDisplay !== "deal_id"}
                      onClick={() => setIdDisplay("deal_id")}
                    >
                      Deal ID
                    </Button>
                    <Button
                      id="btn-ulid"
                      color={idDisplay === "uid" ? "primary" : "secondary"}
                      outline={idDisplay !== "uid"}
                      onClick={() => setIdDisplay("uid")}
                    >
                      ULID
                    </Button>
                  </ButtonGroup>
                  <UncontrolledTooltip target="btn-deal-id">Show deal ID in first column</UncontrolledTooltip>
                  <UncontrolledTooltip target="btn-ulid">Show ULID in first column</UncontrolledTooltip>
                </Col>
                {hasActiveFilters && (
                  <Col md="auto">
                    <Button size="sm" color="link" className="text-primary p-0" onClick={handleClearFilters}>
                      <i className="fas fa-times mr-1" />
                      Clear filters
                    </Button>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              <Table hover responsive>
                <thead>
                  <tr>
                    <SortableHeader
                      label={idDisplay === "uid" ? "ULID" : "Deal ID"}
                      field={idDisplay}
                      ordering={ordering}
                      onSort={handleSort}
                    />

                    <SortableHeader label="Location" field="location" ordering={ordering} onSort={handleSort} />
                    <SortableHeader label="Email" field="client_email" ordering={ordering} onSort={handleSort} />
                    <SortableHeader label="Created at" field="created_at" ordering={ordering} onSort={handleSort} />
                    <SortableHeader label="Updated at" field="updated_at" ordering={ordering} onSort={handleSort} />
                    <th>Step</th>
                  </tr>
                </thead>
                <tbody>
                  {installations.map((inst) => (
                    <tr
                      key={inst.uid}
                      role="button"
                      style={{ cursor: "pointer" }}
                      onClick={() => inst.uid && history.push(`/installations/${inst.uid}`)}
                    >
                      <td>{idDisplay === "uid" ? inst.uid : inst.deal_id}</td>
                      <td>
                        <a
                          href={`/stores?search=${inst.location}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-info"
                        >
                          {inst.location}
                        </a>
                      </td>
                      <td>{inst.client_email}</td>
                      <td>{formatDate(inst.created_at)}</td>
                      <td>{formatDate(inst.updated_at)}</td>
                      <td>
                        <StepBadge step={inst.step} />
                      </td>
                    </tr>
                  ))}
                  {installations.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        No installations found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
